import React, { useEffect, useState } from "react";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { useOrganizationVendors, usePrimaryOrganization } from "../../hooks";
import { RequestHandlingInstructionDto } from "../../../common/service/server/dto/RequestHandlingInstructionDto";
import { useRequestInstructions } from "../../hooks/useOrganizationVendors";
import { useActionRequest, useDataRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { Slideout, StepNumberCircle, Toggle } from "@truevault/aquilo";
import {
  EmailTemplateWithCustomTextInputField,
  TRUEVAULT_BADGE_ICON,
} from "./DeleteInstructionsPage";
import {
  CollectionContext,
  OrganizationDataRecipientDto,
} from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import {
  ArrowTurnDownRightIcon,
  BoltIcon,
  BoltSlashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { Form } from "react-final-form";
import { PageLoading } from "../../components";
import "./vendor-instructions.css";
import clsx from "clsx";
import { useSnackBar } from "@truevault/aquilo";
import { WorkflowSetupPageWrapper } from "./WorkflowSetupPageWrapper";
import { RequestHandlingInstructionsTextarea } from "./RequestHandlingInstructionsTextarea";
import { buildStepsFromWorkflowType } from "./HorizontalStepProgressIndicator";
import { WorkflowType } from "../../../common/service/server/dto/RegulationRequestTypeDto";
import { alphabetically } from "../../surveys/util";
import { useIntegrationsSettings } from "../../components/superorg/settings/IntegrationsSettings";
import { useAvailableIntegrationVendorIds } from "../../hooks/useAvailableIntegrationVendorIds";
import { PrivacyCenterIntegrationsDto } from "../../../common/service/server/dto/PrivacyCenterIntegrationsDto";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { NetworkRequest } from "../../../common/models";
import { canBeAccessDataSource, isVendorDataAccessibleForWorkflow } from "../../util/vendors";
import { useSurvey } from "../../../common/hooks/api/useSurvey";
import { useManagedAdNetworks } from "../../../common/hooks/dataRecipientSettingsHooks";
import { OrganizationManagedAdNetworkDto } from "../../../common/service/server/dto/OrganizationManagedAdNetworkDto";
import { SELLING_AND_SHARING_SURVEY_NAME } from "../get-compliant/data-recipients/shared";
import { getVendorNesting, isConnectedSystemVendor } from "../../util/vendorNesting";
import { useDataRetrievedVia, useSetDataRetrievedVia } from "../../hooks/useManagedAdNetworks";
import { DataRetrievedViaPicker } from "./DataRetrievedViaPicker";
import {
  WorkflowTableOpenCell,
  WorkflowTablePill,
  workflowTableCellStyle,
  workflowTableClasses,
  workflowTableHeaderCellStyle,
  workflowTableVendorCellStyle,
} from "./workflowSetupTable";

type AccessRequestInstructionsPageProps = {
  collectionContext?: CollectionContext[];
  embedded?: boolean;
};

export const AccessRequestInstructionsPage = ({
  collectionContext = ["CONSUMER"],
  embedded = false,
}: AccessRequestInstructionsPageProps = {}) => {
  const snackbar = useSnackBar();

  const [organization, , refreshOrg] = usePrimaryOrganization();

  const [showFooter, setShowFooter] = useState(true);

  const [
    autoSendRequestEmailNotificationsForAccessRequestsValue,
    setAutoSendRequestEmailNotificationsForAccessRequestsValue,
  ] = useState(null);

  useEffect(() => {
    if (organization) {
      setAutoSendRequestEmailNotificationsForAccessRequestsValue(
        organization.autoSendRequestEmailNotificationsForAccessRequests,
      );
    }
  }, [organization]);

  const { result: consumerRequestClosedKnowSampleMessage, refresh: refreshConsumerMessage } =
    useDataRequest({
      queryKey: ["emailTemplate", organization?.id, "closed-know"],
      api: () => Api.organization.getOrganizationMessageSample(organization?.id, "closed-know"),
    });

  const showCustomTextInputField =
    consumerRequestClosedKnowSampleMessage?.templateHTML?.includes("[[[CUSTOM_TEXT]]]");

  if (!organization) {
    if (embedded) {
      return <PageLoading />;
    }

    return (
      <StandardPageWrapper>
        <PageLoading />
      </StandardPageWrapper>
    );
  }

  const accessStepProgressSteps = buildStepsFromWorkflowType(WorkflowType.KNOW, 1);
  const handleStepProgressClick = (stepId: string) => {
    document.getElementById(stepId)?.scrollIntoView({ behavior: "smooth" });
  };

  const pageContent = (
    <>
      <div id="verify-consumer" className="tw-boundary">
        <div className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="1" />
          <div className="text-neutral-900 text-[18px] font-normal">
            Review your vendors with accessible data and their access instructions
          </div>
        </div>

        <div className="ml-[40px] mt-[6px] max-w-[736px] text-neutral-600 text-[14px] font-normal">
          If a vendor&apos;s data is already in another vendor, like FedEx shipping data in Shopify,
          set <span className="font-medium text-neutral-900">Data retrieved via</span>. It&apos;s
          listed under that vendor on access requests, so you answer once for both.
        </div>

        <div className="mt-3 ml-[40px]">
          <AccessRequestInstructionsVendorsTable
            orgId={organization.id}
            vendorsCollectionContexts={collectionContext}
            onSidebarChange={(isOpen: boolean) => {
              setShowFooter(!isOpen);
            }}
          />
        </div>

        <div id="retrieve-data" className="flex items-center gap-3 mt-10">
          <StepNumberCircle text="2" />
          <div className="text-gray-900 text-[18px] font-normal">
            Auto-send request processing emails
          </div>
        </div>
        <div className="ml-[40px] mt-[6px]">
          <span className="flex items-center gap-2">
            <span className="text-neutral-900 text-sm font-normal">Automate this step</span>
            <Toggle
              checked={autoSendRequestEmailNotificationsForAccessRequestsValue}
              onChange={async (checked) => {
                setAutoSendRequestEmailNotificationsForAccessRequestsValue(checked);
                await Api.organization.updateOrganization(organization.id, {
                  name: organization.name,
                  logoUrl: organization.logoUrl,
                  regulations: [],
                  autoSendRequestEmailNotificationsForAccessRequests: checked,
                });
                await refreshOrg();

                if (checked) {
                  snackbar("Enabled automatically sending access request email notifications", {
                    variant: "success",
                  });
                } else {
                  // closeSnackbar functionality not available in new API
                }
              }}
            />
          </span>
        </div>
        <div className="text-neutral-900 font-normal text-[14px] max-w-[950px] ml-[40px] mt-[6px]">
          If this is toggled off, you will be prompted to manually click a button to send the vendor
          email and consumer notification email below for each request you process.
        </div>
      </div>

      {/* Put the email templates outside of tw-boundary since the template HTML will only render correctly with default styles, and will not render correctly if overriden with tailwind styles */}
      <div id="send-data" style={{ marginTop: "12px", marginLeft: "40px", marginBottom: "700px" }}>
        <div className="tw-boundary">
          <div className="text-neutral-900 text-[14px]">Consumer Email Notification Template</div>
        </div>

        <div
          style={{
            backgroundColor: "#F9F8FB",
            padding: "32px",
            maxWidth: "736px",
            marginTop: "6px",
          }}
        >
          <p>
            <strong>To:</strong> <span>consumer@example.com</span>
          </p>

          <p>
            <strong>Subject: A message about your request [REQUEST ID#]</strong>
          </p>

          {showCustomTextInputField ? (
            <EmailTemplateWithCustomTextInputField
              orgId={organization.id}
              consumerRequestSampleMessage={consumerRequestClosedKnowSampleMessage}
              refreshConsumerMessage={refreshConsumerMessage}
              messageSlug="closed-know"
            />
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: consumerRequestClosedKnowSampleMessage?.templateHTML,
              }}
            ></div>
          )}
        </div>
      </div>
    </>
  );

  if (embedded) {
    return pageContent;
  }

  return (
    <WorkflowSetupPageWrapper
      selectedTabKey="access-request"
      nextOnClick={async () => {}}
      nextDisabled={false}
      hideFooter={!showFooter}
      stepProgressSteps={accessStepProgressSteps}
      onStepProgressClick={handleStepProgressClick}
    >
      {pageContent}
    </WorkflowSetupPageWrapper>
  );
};

export function AccessRequestInstructionsVendorsTable({
  orgId,
  vendorsCollectionContexts,
  onSidebarChange,
}: {
  orgId: OrganizationPublicId;
  vendorsCollectionContexts: CollectionContext[];
  onSidebarChange?: (isOpen: boolean) => void;
}) {
  const [allVendors, vendorsRequest] = useOrganizationVendors(
    orgId,
    // All following args are default values from function signature, except for onlyUseSettled
    /* excludeRecipientCategories = */ [],
    /* includeDataStorageLocations = */ false,
    /* excludeIncomplete = */ undefined,
    vendorsCollectionContexts,
    /* onlyUseSettled = */ true,
  );
  const [instructions, , fetchInstructions] = useRequestInstructions(
    orgId,
    "KNOW",
    undefined,
    true,
  );
  const { fetch: updateInstruction } = useActionRequest({
    api: async (newInstruction: RequestHandlingInstructionDto) => {
      await Api.requestHandlingInstructions.putInstruction(orgId, newInstruction);
    },
    onSuccess: fetchInstructions,
    // onError: () => {
    //   enqueueSnackbar(`Error updating vendor deletion settings`, { variant: "error" });
    // },
  });

  // Which vendors belong here only needs the session-cached integration availability the request workflows use. The
  // full integrations settings call skips that cache and queries Paragon live, so it only feeds the connection icons
  // and the table does not wait for it.
  const { integrationAvailability, isReady: integrationAvailabilityReady } =
    useAvailableIntegrationVendorIds(true, { failFast: true });
  const { settings: apiIntegrations } = useIntegrationsSettings();
  const vendorAndIntegrationsReady =
    NetworkRequest.areFinished(vendorsRequest) && integrationAvailabilityReady;

  const { answers: sellingAndSharingAnswers } = useSurvey(orgId, SELLING_AND_SHARING_SURVEY_NAME);
  const { managedAdNetworks } = useManagedAdNetworks(orgId);
  const { data: loadedDataRetrievedVia, isError: dataRetrievedViaFailed } =
    useDataRetrievedVia(orgId);
  // If the links fail to load, show every vendor on its own rather than hiding the table.
  const dataRetrievedVia = loadedDataRetrievedVia ?? (dataRetrievedViaFailed ? [] : undefined);
  const { mutateAsync: setDataRetrievedVia } = useSetDataRetrievedVia(orgId);
  const snackbar = useSnackBar();
  const canBeAccessSource = (vendor: OrganizationDataRecipientDto) =>
    canBeAccessDataSource(vendor, integrationAvailability);
  const nesting = getVendorNesting(allVendors, managedAdNetworks, sellingAndSharingAnswers, {
    isNestedVendorRelevant: (vendor) =>
      isVendorDataAccessibleForWorkflow(vendor, integrationAvailability),
    dataRetrievedVia,
    isDataRetrievedViaEligible: canBeAccessSource,
  });

  const updateDataRetrievedVia = async (networkId: string, managerId: string | null) => {
    try {
      await setDataRetrievedVia({ networkId, managerId });
    } catch (e) {
      snackbar("Couldn't update Data retrieved via. Try again.", { variant: "error" });
    }
  };

  let accessibleVendors = vendorAndIntegrationsReady
    ? allVendors
        ?.filter(
          (vendor) =>
            !nesting.isManaged(vendor.id) &&
            (nesting.isActiveManager(vendor.id) ||
              isVendorDataAccessibleForWorkflow(vendor, integrationAvailability)),
        )
        ?.sort(alphabetically)
    : undefined;

  const [sidebarOpenForVendorId, setSidebarOpenForVendorId] = useState<string | null>(null);

  // A vendor another vendor already handles requests for (an ad network under its agency, say) can't be picked as a
  // source of data: it has no row of its own on the request, so the link would be ignored.
  const unavailableDataRetrievedVia: Record<string, string> = {};
  nesting.activeManagers.forEach((manager) =>
    nesting.getManagedVendorsForManager(manager.id).forEach((managed) => {
      unavailableDataRetrievedVia[managed.id] = `Managed by ${manager.name}`;
    }),
  );
  allVendors?.forEach((vendor) => {
    if (!unavailableDataRetrievedVia[vendor.id] && !canBeAccessSource(vendor)) {
      unavailableDataRetrievedVia[vendor.id] = "No accessible data for access requests";
    }
  });

  if (
    !allVendors ||
    !instructions ||
    !managedAdNetworks ||
    !dataRetrievedVia ||
    !vendorAndIntegrationsReady
  ) {
    return null;
  }

  // Each top-level vendor is followed by the vendors whose data is retrieved via it (DES-43). They stay in the table,
  // indented, so they can still be opened and unlinked.
  const rows = (accessibleVendors ?? []).flatMap((dataRecipient) => [
    { dataRecipient, retrievedVia: null as OrganizationDataRecipientDto | null },
    ...nesting
      .getRetrievedViaVendorsForManager(dataRecipient.id)
      .sort(alphabetically)
      .map((nested) => ({ dataRecipient: nested, retrievedVia: dataRecipient })),
  ]);

  const headerCellClassName = workflowTableClasses.headerCell;
  const headerCellStyle = workflowTableHeaderCellStyle;

  return (
    <div className={clsx(workflowTableClasses.container, "max-w-[736px]")}>
      <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <colgroup>
          <col style={{ width: "250px" }} />
          <col style={{ width: "150px" }} />
          <col style={{ width: "276px" }} />
          <col style={{ width: "60px" }} />
        </colgroup>
        <thead className="sticky top-0 z-10">
          <tr>
            <th
              scope="col"
              className={clsx(headerCellClassName, "pl-6 text-left")}
              style={headerCellStyle}
            >
              Vendor
            </th>
            <th
              scope="col"
              className={clsx(headerCellClassName, "text-center")}
              style={headerCellStyle}
            >
              Access Instructions
            </th>
            <th
              scope="col"
              className={clsx(headerCellClassName, "text-left pl-4")}
              style={headerCellStyle}
            >
              Data retrieved via
            </th>
            <th scope="col" className={headerCellClassName} style={headerCellStyle}>
              &nbsp;
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ dataRecipient, retrievedVia }) => {
            let knowInstruction = instructions?.find(
              (i) => i.vendorId == dataRecipient.vendorId && i.requestType == "KNOW",
            );

            return (
              <DataRecipientRowWithSidebar
                key={dataRecipient.id}
                organizationId={orgId}
                dataRecipient={dataRecipient}
                knowInstruction={knowInstruction}
                updateInstruction={updateInstruction}
                openSidebar={() => {
                  setSidebarOpenForVendorId(dataRecipient.vendorId);
                  if (onSidebarChange) {
                    onSidebarChange(true);
                  }
                }}
                isSidebarOpen={sidebarOpenForVendorId === dataRecipient.vendorId}
                closeSidebar={() => {
                  setSidebarOpenForVendorId(null);
                  if (onSidebarChange) {
                    onSidebarChange(false);
                  }
                }}
                apiIntegrations={apiIntegrations}
                managedVendors={nesting.getManagedVendorsForManager(dataRecipient.id)}
                retrievedVia={retrievedVia}
                pickerVendors={allVendors}
                unavailableDataRetrievedVia={unavailableDataRetrievedVia}
                dataRetrievedVia={dataRetrievedVia}
                onDataRetrievedViaChange={(managerId) =>
                  updateDataRetrievedVia(dataRecipient.id, managerId)
                }
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DataRecipientRowWithSidebar({
  organizationId,
  dataRecipient,
  knowInstruction,
  updateInstruction,
  isSidebarOpen,
  openSidebar,
  closeSidebar,
  apiIntegrations,
  managedVendors,
  retrievedVia,
  pickerVendors,
  unavailableDataRetrievedVia,
  dataRetrievedVia,
  onDataRetrievedViaChange,
}: {
  organizationId: string;
  dataRecipient: OrganizationDataRecipientDto;
  knowInstruction: RequestHandlingInstructionDto;
  updateInstruction: (instruction: RequestHandlingInstructionDto) => void;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  apiIntegrations: PrivacyCenterIntegrationsDto;
  managedVendors: OrganizationDataRecipientDto[];
  /** The vendor this row's data is retrieved via, when the row is nested under it (DES-43). */
  retrievedVia: OrganizationDataRecipientDto | null;
  pickerVendors: OrganizationDataRecipientDto[];
  /** Vendors that can't be picked as a source of data, with the reason shown in the picker. */
  unavailableDataRetrievedVia: Record<string, string>;
  dataRetrievedVia: OrganizationManagedAdNetworkDto[];
  onDataRetrievedViaChange: (managerId: string | null) => Promise<void>;
}) {
  const [userProvidedAccessInstruction, setUserProvidedAccessInstruction] = useState(
    knowInstruction?.processingInstructions ?? "",
  );

  const knowInstructionWithLocalChanges: RequestHandlingInstructionDto = knowInstruction
    ? {
        ...knowInstruction,
        processingInstructions: userProvidedAccessInstruction,
      }
    : {
        organizationId: organizationId,
        vendorId: dataRecipient.vendorId,
        requestType: "KNOW",
        processingInstructions: userProvidedAccessInstruction,
      };

  let hasVendorAccessInstructions = dataRecipient.accessInstructions?.length > 0;
  // A vendor retrieved via another needs no instructions of its own, but can still have them. Links are org-wide, so
  // only count one that takes effect in this table (e.g. a consumer-only source doesn't cover an HR request).
  const isLinked = retrievedVia !== null;
  const retrievedViaThis = dataRetrievedVia.filter(
    (relation) => relation.managerId === dataRecipient.id,
  );
  const retrievedViaNames = retrievedViaThis.map((relation) => relation.networkName).join(", ");
  // Some vendors keep their own row and can't be linked: links stay one level deep (the API refuses chains too), and a
  // vendor that handles requests for others would otherwise be listed twice.
  const notLinkableReason = isConnectedSystemVendor(dataRecipient)
    ? `${dataRecipient.name} handles requests through its automation, so it can't be retrieved via another vendor.`
    : managedVendors.length > 0
    ? `${dataRecipient.name} handles requests for ${managedVendors
        .map((vendor) => vendor.name)
        .join(", ")}, so it can't be retrieved via another vendor.`
    : retrievedViaThis.length > 0
    ? `${retrievedViaNames} ${retrievedViaThis.length > 1 ? "are" : "is"} retrieved via ${
        dataRecipient.name
      }, so ${dataRecipient.name} can't be retrieved via another vendor.`
    : null;

  const dataRetrievedViaField = notLinkableReason ? (
    <span className="text-neutral-500 text-sm" title={notLinkableReason}>
      This vendor
    </span>
  ) : (
    <DataRetrievedViaPicker
      vendor={dataRecipient}
      vendors={pickerVendors}
      relations={dataRetrievedVia}
      unavailableReasons={unavailableDataRetrievedVia}
      onChange={onDataRetrievedViaChange}
      compact
    />
  );

  let vendorApiConnection = (apiIntegrations?.vendors || []).find(
    (apiIntegration) => apiIntegration.vendorId == dataRecipient.vendorId,
  );

  return (
    <>
      <tr
        key={dataRecipient.id}
        className={clsx(workflowTableClasses.row, "cursor-pointer", {
          "bg-neutral-0": isSidebarOpen,
        })}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          openSidebar();
        }}
      >
        <td
          className={workflowTableClasses.cell}
          style={{ ...workflowTableVendorCellStyle, paddingLeft: retrievedVia ? 40 : 24 }}
        >
          <div className="flex items-center gap-[12px]">
            {retrievedVia && (
              <ArrowTurnDownRightIcon
                className="h-4 w-4 flex-shrink-0 text-neutral-400"
                aria-label={`Retrieved via ${retrievedVia.name}`}
              />
            )}
            <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-6 w-6" />
            <span
              className={clsx(
                "font-medium text-[14px]",
                retrievedVia ? "text-neutral-700" : "text-[#111827]",
              )}
            >
              {dataRecipient.name}
            </span>
            {vendorApiConnection &&
              (vendorApiConnection.connected ? (
                <BoltIcon className="h-4 w-4 text-green-600 bg-[#E2FDF3] p-0.5 rounded-3xl" />
              ) : (
                <BoltSlashIcon className="h-4 w-4 text-neutral-700 bg-[#F5F4F9] p-0.5 rounded-3xl" />
              ))}
          </div>
          {managedVendors.length > 0 && (
            <div className="mt-2 text-gray-600 text-xs font-medium">
              Managing: {managedVendors.map((vendor) => vendor.name).join(", ")}
            </div>
          )}
        </td>
        <td
          className={clsx(workflowTableClasses.cell, "text-center")}
          style={{ ...workflowTableCellStyle, paddingLeft: 12, paddingRight: 12 }}
        >
          {accessInstructionsColumnValue(dataRecipient, knowInstructionWithLocalChanges, isLinked)}
        </td>
        <td
          className={workflowTableClasses.cell}
          // The picker is taller than a line of text, so trim the vertical padding to keep rows the same height.
          style={{ paddingTop: 12, paddingBottom: 12, paddingLeft: 16, paddingRight: 8 }}
          // The picker sits inside a clickable row; keep its clicks from opening the side panel.
          onClick={(e) => e.stopPropagation()}
        >
          {dataRetrievedViaField}
        </td>
        <WorkflowTableOpenCell onOpen={openSidebar} vendorName={dataRecipient.name} />
      </tr>

      <Slideout
        open={isSidebarOpen}
        setOpen={(newIsOpen: boolean) => {
          if (newIsOpen) {
            openSidebar();
          } else {
            closeSidebar();
          }
        }}
        slideFrom="right"
      >
        <>
          <div className="bg-neutral-0 p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-gray-900 text-[18px] font-bold">
                Access Request Vendor Settings
              </h3>
              <div className="cursor-pointer" onClick={closeSidebar}>
                <XMarkIcon className="w-6 h-6 text-[#9CA3AF]" />
              </div>
            </div>

            <div className="mt-1">
              <div className="flex items-center">
                <img src={dataRecipient.logoUrl} alt={dataRecipient.name} className="h-5 w-5" />
                <span className="text-gray-900 font-medium text-[18px] ml-3">
                  {dataRecipient.name}
                </span>
              </div>
              {dataRecipient.url?.length > 0 && (
                <div className="mt-1 text-[14px] font-normal text-gray-500">
                  {dataRecipient.url}
                </div>
              )}
            </div>
          </div>
          <div>
            <Form
              onSubmit={() => {}}
              render={() => {
                return (
                  <>
                    <div className="px-6 pt-6">
                      <div className="text-[14px] text-gray-700 font-medium">
                        Data retrieved via
                      </div>
                      <div className="mt-1 text-[14px] text-gray-500">
                        {notLinkableReason ??
                          `If ${dataRecipient.name}'s data is already in another vendor, pick that vendor. ${dataRecipient.name} will be listed under it on access requests, so you answer once for both.`}
                      </div>
                      {!notLinkableReason && (
                        <div className="mt-3">
                          <DataRetrievedViaPicker
                            vendor={dataRecipient}
                            vendors={pickerVendors}
                            relations={dataRetrievedVia}
                            unavailableReasons={unavailableDataRetrievedVia}
                            onChange={onDataRetrievedViaChange}
                          />
                          <div className="mt-1.5 text-xs text-gray-500">
                            Applies to access and portability requests only.
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between items-center">
                        <div className="text-[14px] text-gray-700 font-medium">
                          Self Service Access Instructions
                        </div>
                        {hasVendorAccessInstructions && (
                          <div className="flex items-center gap-1">
                            {TRUEVAULT_BADGE_ICON}
                            <span className="text-primary-600 text-[10px] font-medium">
                              Verified
                            </span>
                          </div>
                        )}
                      </div>
                      {hasVendorAccessInstructions && (
                        <div className="vendor-instructions mt-3">
                          <div
                            dangerouslySetInnerHTML={{ __html: dataRecipient.accessInstructions }}
                          />
                        </div>
                      )}
                      <div className="mt-4">
                        <div className="text-neutral-700 text-[14px] font-medium">
                          {hasVendorAccessInstructions || isLinked
                            ? "Add Your Access Instructions (optional)"
                            : "Add Your Access Instructions"}
                        </div>
                        <RequestHandlingInstructionsTextarea
                          rows={4}
                          className="block w-full rounded-md bg-white px-3 py-1.5 text-[14px] text-neutral-800 placeholder:text-neutral-600 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                          defaultValue={userProvidedAccessInstruction}
                          placeholder="E.g. find the consumer first at the settings.."
                          onBlur={async (e: React.FocusEvent<HTMLTextAreaElement>) => {
                            let newUserProvidedKnowInstruction = e.target.value;

                            setUserProvidedAccessInstruction(newUserProvidedKnowInstruction);

                            let updatedInstruction = {
                              ...knowInstructionWithLocalChanges,
                              processingInstructions: newUserProvidedKnowInstruction,
                            };

                            await updateInstruction(updatedInstruction);
                          }}
                        />
                      </div>
                    </div>
                  </>
                );
              }}
            />
          </div>
        </>
      </Slideout>
    </>
  );
}

const accessInstructionsColumnValue = (
  vendor: OrganizationDataRecipientDto,
  knowInstruction: RequestHandlingInstructionDto,
  isRetrievedViaAnother: boolean,
): React.ReactNode => {
  let resultLabel: React.ReactNode = "",
    textColor = "",
    bgColor = "";

  if (vendor?.accessInstructions) {
    resultLabel = (
      <div className="flex items-center gap-1">
        {TRUEVAULT_BADGE_ICON}
        <span className="text-primary-600">Verified</span>
      </div>
    );
    bgColor = "bg-[#EDE9FE]";
  } else if (knowInstruction?.processingInstructions?.length > 0) {
    resultLabel = "Your Instructions";
    textColor = "text-blue-800";
    bgColor = "bg-blue-100";
  } else if (isRetrievedViaAnother) {
    resultLabel = "Optional";
    textColor = "text-gray-800";
    bgColor = "bg-gray-100";
  } else {
    resultLabel = "Add Instructions";
    textColor = "text-amber-800";
    bgColor = "bg-amber-100";
  }

  return <WorkflowTablePill className={clsx(textColor, bgColor)}>{resultLabel}</WorkflowTablePill>;
};
